import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DateTimeHelper } from '../../../common/helpers/datetime/datetime.helper';
import { LoadingIndicatorService, ToastService } from '../../../main/services';
import { ListContracts, Round, Tender } from '../../models';
import { ROUND_STATUSES } from "../../models/round";
import { TenderBidsService } from '../../services';

@Component({
    selector: 'jft-tender-contracts-expired',
    templateUrl: './tender-contracts-expired.component.html',
    styleUrls: ['./tender-contracts-expired.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TenderContractsExpiredComponent implements OnInit, OnDestroy {

    public loading: boolean = false;
    public tender: Tender;
    public contracts: ListContracts[] = [];
    public roundStatuses = ROUND_STATUSES;

    public orderField: string;
    public order: string;

    private termSubscription: Subscription;
    private updateContractsSubscription: Subscription;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        protected toast: ToastService,
        private contractsService: TenderBidsService,
        private loadingIndicator: LoadingIndicatorService,
    ) {
    }

    public ngOnInit() {
        this.dataInit();
        this.orderInit();
        this.searchInit();
    }

    private dataInit() {
        let data = this.route.snapshot.data,
            parentData = this.route.parent.parent.snapshot.data;

        // Don't load contracts from resolver - let parent component's loadLocationTags() trigger it
        // This ensures saved tag filters are loaded before fetching data
        this.tender = parentData['tender'];

        // Subscribe to updates from parent component's filter changes
        this.updateContractsSubscription = this.contractsService.updateContractsObservable.subscribe(item => {
            // Only update contracts if it's not a stats_updated event
            if (!item || (typeof item === 'object' && (item as any).type !== 'stats_updated')) {
                this.refreshContractsWithFilters();
            }
        });
    }

    private refreshContractsWithFilters() {
        this.loading = true;
        this.loadingIndicator.show();

        this.contractsService.fetchExpired(this.tender.id).subscribe((result: any) => {
            // Handle both paginated and non-paginated responses
            if (Array.isArray(result)) {
                this.contracts = result;
            } else {
                this.contracts = result.contracts;
            }
            this.loading = false;
            this.loadingIndicator.hide();
        }, (error) => {
            this.loading = false;
            this.loadingIndicator.hide();
            this.toast.error(error, this.tender.name);
        });
    }

    private searchInit() {
        this.termSubscription = this.contractsService.termObservable
            .pipe(debounceTime(500))
            .pipe(distinctUntilChanged())
            .subscribe((term: string) => {
                this.loading = true;
                this.loadingIndicator.show();

                this.contractsService.setFilter(term);
                this.contractsService.fetchExpired(this.tender.id).subscribe((result: any) => {
                    // Handle both paginated and non-paginated responses
                    if (Array.isArray(result)) {
                        this.contracts = result;
                    } else {
                        this.contracts = result.contracts;
                    }
                    this.loading = false;
                    this.loadingIndicator.hide();
                }, (error) => {
                    this.loading = false;
                    this.loadingIndicator.hide();
                    this.toast.error(error, this.tender.name);
                });
            });
    }

    private orderInit() {
        let order = this.contractsService.getOrder();
        if (order) {
            this.order = order.startsWith('-') ? 'DESC' : 'ASC';
            this.orderField = order.startsWith('-') ? order.slice(1) : order;
        }
    }

    public sort($event: Event, field: string) {
        $event.preventDefault();
        $event.stopPropagation();
        if (this.orderField == field) {
            this.order = this.order == 'ASC' ? 'DESC' : 'ASC';
        } else {
            this.order = 'ASC';
        }
        this.orderField = field;

        this.loading = true;
        this.loadingIndicator.show();

        this.contractsService.setOrder(this.orderField, this.order).fetchExpired(this.tender.id).subscribe((result: any) => {
            // Handle both paginated and non-paginated responses
            if (Array.isArray(result)) {
                this.contracts = result;
            } else {
                this.contracts = result.contracts;
            }
            this.loading = false;
            this.loadingIndicator.hide();
        }, (e) => {
            this.loading = false;
            this.loadingIndicator.hide();
            this.toast.error(e, this.tender.name);
        });
    }

    public onRoundExtend(round: Round) {
        this.router.navigateByUrl(`/tenders/${this.tender.id}/locations/all`)
    }

    public ngOnDestroy(): void {
        this.termSubscription.unsubscribe();
        this.updateContractsSubscription.unsubscribe();
    }

    public isDateExpired(date) {
        return DateTimeHelper.isDateExpired(date)
    }
}
